import { BooleanSchema, StringSchema, boolean, object, string } from 'yup'

import { transformErrors } from 'utils/errors'
import { noSpecialCharacters } from 'utils/validation-schemas/helpers'

import { passwordValidationString, requiredString } from '../common/utils'
import { isClickId } from './form'

noSpecialCharacters()

export const INITIAL_VALUES = {
  email: '',
  firstName: '',
  lastName: '',
  password: '',
  usResident: false,
  eSignConsent: false,
  partnerDisclosure: false,
}

export const validationErrorMap = new Map<string, string>([
  ['email', 'Please enter a valid email'],
  ['password', 'Please enter a valid password'],
  [
    'usResident',
    'To proceed, please confirm that you are a U.S. resident, at least 18 years old, and agree to the Program Agreement, Privacy Policy, Truth in Savings Disclosure, Terms and Conditions, Electronic Funds Transfers Disclosure, Spend Funds Availability, LSB Privacy Policy, and Auto Debit Authorization.',
  ],
  ['partnerDisclosure', 'You must read the partner referral disclosure to join Acorns'],
  ['eSignConsent', 'Please confirm that you agree to the E-Sign Consent.'],
])

const firstNameMaxCharsValidationMessage = 'Your first name cannot exceed ${max} characters'
const lastNameMaxCharsValidationMessage = 'Your last name cannot exceed ${max} characters'
const emailValidationMessage = 'Please enter a valid email'
const emailMaxValidationMessage = 'Email must be ${max} characters or less.'
const checkboxValidationMessage = 'This checkbox is required'

type ValidationSchema = {
  email: StringSchema
  firstName: StringSchema
  lastName: StringSchema
  password: StringSchema
  usResident: BooleanSchema
  eSignConsent: BooleanSchema
  partnerDisclosure?: BooleanSchema
}

// Handles Formik's validate event.
export const validate = (values, props) => {
  let errors = {}

  try {
    validationSchema(props).validateSync(values, { abortEarly: false })
  } catch (validationErrors) {
    // Convert validation errors to Formik errors collection
    errors = transformErrors(validationErrors.inner)
  }

  return errors
}

// Validation schema to be used by Formik.
export const validationSchema = ({ partner }) => {
  const schema: ValidationSchema = {
    email: requiredString()
      .email(emailValidationMessage)
      .max(65, emailMaxValidationMessage)
      .excludeSpecialCharacters(),
    firstName: string()
      .required('Please enter a valid first name')
      .max(32, firstNameMaxCharsValidationMessage)
      .excludeSpecialCharacters(),
    lastName: string()
      .required('Please enter a valid last name')
      .max(32, lastNameMaxCharsValidationMessage)
      .excludeSpecialCharacters(),
    password: passwordValidationString(),
    usResident: boolean().oneOf([true], checkboxValidationMessage),
    eSignConsent: boolean().oneOf([true], checkboxValidationMessage),
  }

  // If the customer is registering through a partner, the schema is extended
  // to include the "partnerDisclosure" field
  if (partner || isClickId()) {
    schema.partnerDisclosure = boolean().oneOf([true], checkboxValidationMessage)
  }

  return object().shape(schema)
}
